import {
  Children,
  PointerEvent,
  ReactElement,
  RefAttributes,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import { getNumbers, getOf, useTokenValues } from '@jds/react-engine';

import type { CarouselProps } from './interface';
import { CarouselSlideProps } from '../jdscarouselslide/logic';
import { Devices } from '../../service/Devices';
import { useRootRef } from '../../hooks/useRootRef';

export type CarouselRef = {
  next: CarouselProps['prev'];
  prev: CarouselProps['next'];
  goToSlide: CarouselProps['goToSlide'];
  getCurrentIndex: CarouselProps['getCurrentIndex'];
  getCaption: CarouselProps['getCaption'];
};

const clampValue = (index: number, total: number) => {
  if (index < 0) return total - 1;
  if (index >= total) return 0;
  return index;
};

const sortedOrder = (children: Array<ReactElement<CarouselSlideProps>>) => {
  children.sort((a, b) => {
    const elementA = a as ReactElement<CarouselSlideProps>;
    const elementB = b as ReactElement<CarouselSlideProps>;
    return (elementA.props.order || -1) - (elementB.props.order || -1);
  });
  return children as Array<ReactElement<CarouselSlideProps>>;
};

const AUTOPLAY_DURATION = 3000;
const TRANSITION_DURATION = 300;
const TOTALSPINNER_VALUE = 360;

export const withLogic = (
  Component: React.ComponentType<CarouselProps>
): React.ForwardRefExoticComponent<
  Omit<CarouselProps, 'ref'> & RefAttributes<CarouselRef>
> =>
  forwardRef<CarouselRef, CarouselProps>((userProps, ref) => {
    const {
      activeIndex = -1,
      initialIndex = 0,
      slideDuration = AUTOPLAY_DURATION,
      hideNextSlide,
      totalSlides: slideCount = 0,
      ...props
    } = userProps;

    const SLIDE_PERCENT = Devices.isMobile() && !hideNextSlide ? 92 : 100;

    const totalSlides = slideCount || Children.count(props.children);
    const [spinnerValue, setSpinnerValue] = useState(TOTALSPINNER_VALUE);
    const { element, rootRef } = useRootRef();
    const transitionProgressRef = useRef(false);

    const intervalRef = useRef<NodeJS.Timeout>();
    const spinnerIntervalRef = useRef<NodeJS.Timeout>();

    const [selected, setSelected] = useState(props.playing);

    useEffect(() => {
      handleAutoPlay(Boolean(props.playing));
    }, [props.playing]);

    const [index, setIndex] = useState(0);

    const touchRef = useRef<{
      start: number;
      end: number;
      started: boolean;
      id: number;
    }>({ start: 0, end: 0, started: false, id: 0 });
    const totalDistance = useRef(0);
    const [totalTouchDistance, setTotalDistance] = useState(0);

    useEffect(() => {
      if (typeof initialIndex !== 'undefined') {
        jumpToSlide(initialIndex);
      }
    }, [initialIndex]);

    useEffect(() => {
      if (element) jumpToSlide(activeIndex);
    }, [activeIndex]);

    const handleEndSlidePositions = (idx?: number) => {
      const currentIndex = idx === undefined ? index : idx;
      const slides: HTMLDivElement[] = Array.from(
        element?.querySelectorAll('div.j-jds-carousel-slide-container') || []
      );
      const firstSlide = slides[0];
      const lastSlide = slides[slides.length - 1];
      if (firstSlide) {
        if (currentIndex === totalSlides - 1) {
          firstSlide.style.transform = `translateX(${
            (totalSlides || 1) * 100
          }%)`;
        } else {
          firstSlide.style.transform = 'unset';
        }
      }
      if (lastSlide) {
        if (currentIndex === 0) {
          lastSlide.style.transform = `translateX(-${
            (totalSlides || 1) * 100
          }%)`;
        } else {
          lastSlide.style.transform = 'unset';
        }
      }
    };

    const enableTransition = () => {
      const containerRef: HTMLDivElement | null | undefined =
        element?.querySelector('#slider-container');

      if (!containerRef) {
        return;
      }
      containerRef.style.transitionDuration = TRANSITION_DURATION + 'ms';
    };

    const next = () => {
      if (transitionProgressRef.current) return;
      const nextIndex = clampValue(index + 1, totalSlides);
      moveToPanel(nextIndex, 1);
      setTimeout(() => {
        handleEndSlidePositions(touchRef.current.id);
      }, TRANSITION_DURATION);
    };

    const prev = () => {
      if (transitionProgressRef.current) return;
      const nextIndex = clampValue(index - 1, totalSlides);
      moveToPanel(nextIndex, -1);
      setTimeout(() => {
        handleEndSlidePositions(touchRef.current.id);
      }, TRANSITION_DURATION);
    };

    function handleImmediateTransitionToSlide(slideIndex: number): void {
      const containerRef: HTMLDivElement | null | undefined =
        element?.querySelector('#slider-container');
      if (!containerRef) {
        return;
      }
      containerRef.style.transitionDuration = '0ms';

      handleEndSlidePositions(totalSlides - 1);
      setTotalDistance(slideIndex * -1 * SLIDE_PERCENT);

      setTimeout(enableTransition, TRANSITION_DURATION);
    }

    const jumpToSlide = (idx: number) => {
      if (transitionProgressRef.current) return;
      const clampedIndex = clampValue(idx, totalSlides);
      if (clampedIndex === index) return;

      moveToPanel(clampedIndex, 1);

      setTimeout(() => {
        handleEndSlidePositions(touchRef.current.id);
      }, TRANSITION_DURATION);
    };

    useImperativeHandle(ref, () => ({
      getCurrentIndex: () => touchRef.current.id,
      goToSlide: jumpToSlide,
      next,
      prev,
      getCaption: (currentIndex: number, _: number) => {
        return (
          (
            Children.toArray(props.children)[
              currentIndex
            ] as ReactElement<CarouselSlideProps>
          ).props.label || ''
        );
      }
    }));

    const moveToPanel = (index: number, dir = 1) => {
      let distance = 0;
      // forwards slide
      if (dir > 0) {
        if (index === 0) {
          // slide to the last slide with transition in the reverse direction and snap to the same slide in forwards direction
          distance = -1 * totalSlides * SLIDE_PERCENT;
          transitionProgressRef.current = true;
          setTimeout(() => {
            handleImmediateTransitionToSlide(0);
            transitionProgressRef.current = false;
          }, TRANSITION_DURATION);
        } else distance = index * -1 * SLIDE_PERCENT;
      } else {
        // backwards slide
        if (totalSlides - 1 === index) {
          distance = SLIDE_PERCENT;
          transitionProgressRef.current = true;
          setTimeout(() => {
            handleImmediateTransitionToSlide(totalSlides - 1);
            transitionProgressRef.current = false;
          }, TRANSITION_DURATION);
        } else distance = index * -1 * SLIDE_PERCENT;
      }
      setTotalDistance(distance);
      setIndex(index);
      touchRef.current.id = index;
    };

    const handlePointerStart = (e: PointerEvent<HTMLDivElement>): void => {
      handleEndSlidePositions();
      touchRef.current.started = true;
      touchRef.current.start = e.clientX;
      totalDistance.current = totalTouchDistance;
    };

    const handlePointerMove = (ev: PointerEvent<HTMLDivElement>): void => {
      const containerWidth = element?.offsetWidth || 1;
      if (touchRef.current.started) {
        touchRef.current.end = ev.clientX;
        const difference =
          (100 / containerWidth) *
          (touchRef.current.end - touchRef.current.start);
        const aactiveSlideDistance = index * -1 * SLIDE_PERCENT;

        setTotalDistance(aactiveSlideDistance + difference);
      }
    };

    const handlePointerEnd = (ev: PointerEvent<HTMLDivElement>): void => {
      const difference = touchRef.current.end - touchRef.current.start;
      const diff = (Math.abs(difference) / ev.currentTarget.offsetWidth) * 100;

      touchRef.current = {
        start: 0,
        started: false,
        end: 0,
        id: touchRef.current.id
      };

      if (Math.abs(diff) > 30) {
        if (difference < 0) {
          next();
        } else {
          prev();
        }
      } else {
        setTotalDistance(totalDistance.current);
      }
    };

    useEffect(() => {
      if (!props.autoPlay) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (spinnerIntervalRef.current)
          clearInterval(spinnerIntervalRef.current);
      }
    }, [props.autoPlay]);

    const handleAutoPlay = (selected: boolean) => {
      setSelected(selected);
      if (!selected) {
        clearInterval(intervalRef.current);
        clearInterval(spinnerIntervalRef.current);
        props?.onPause?.(index);
        setSpinnerValue(0);
      } else {
        props?.onPlay?.(index);
        const animationDuration = slideDuration;

        let startTimestamp: number;

        const animate = (timestamp: number) => {
          if (!startTimestamp) startTimestamp = timestamp;
          const progress = timestamp - startTimestamp;
          const percentage = Math.min(progress / animationDuration, 1); // percentage of animation completion
          const newValue = percentage * 360; // new spinner value based on percentage of completion
          setSpinnerValue(newValue);
          if (progress < animationDuration) {
            // Continue the animation until the duration is reached
            requestAnimationFrame(animate);
          } else {
            // Reset the animation after completion
            startTimestamp = 0;
            setTimeout(animate, slideDuration);
          }
        };

        intervalRef.current = setInterval(() => {
          if (transitionProgressRef.current) return;
          const nextIndex = clampValue(touchRef.current.id + 1, totalSlides);
          moveToPanel(nextIndex, 1);
          setTimeout(() => {
            handleEndSlidePositions(touchRef.current.id);
          }, TRANSITION_DURATION);
        }, slideDuration);

        animate(performance.now());
        spinnerIntervalRef.current = setInterval(() => {
          animate(performance.now());
        }, slideDuration);
      }
    };

    const handleDotClick = (idx: number) => {
      let dir = -1;
      if (idx < index) dir = -1;
      else dir = 1;
      moveToPanel(idx, dir);
      props.onPaginationClick?.();
      handleEndSlidePositions(idx);
    };

    const tokens = useTokenValues();
    const language = tokens.lang;
    const numeralList = useMemo(() => {
      return getNumbers(language);
    }, [language]);
    const localOf = getOf(language);
    const getLocalNumber = (number: number) => {
      const numString = number.toString();
      return numString.replace(
        /\d/g,
        (num) => numeralList[parseInt(num, 10)] ?? num
      );
    };
    return (
      <div
        style={{
          '--move': totalTouchDistance + '%',
          '--slide-width': SLIDE_PERCENT + '%'
        }}
      >
        <Component
          {...{
            ...props,
            totalSlides,
            rootRef,
            children: sortedOrder(
              Children.toArray(props.children) as Array<
                ReactElement<CarouselSlideProps>
              >
            ),
            selectedIndex: index,
            count: totalSlides,
            _subtitle: `${getLocalNumber(index + 1)} ${
              localOf ?? 'of'
            } ${getLocalNumber(totalSlides)}`,
            onNextBtn: () => {
              next();
            },
            onBackBtn: prev,
            onNext: (
              ev: PointerEvent<HTMLDivElement>,
              { type }: { type: 'touchdown' | 'touchmove' | 'touchend' }
            ) => {
              if (type === 'touchdown') {
                handlePointerStart(ev);
              } else {
                if (type === 'touchmove') {
                  handlePointerMove(ev);
                } else {
                  handlePointerEnd(ev);
                }
              }
            },
            playing: selected,
            _value: spinnerValue,
            onPlayPause: () => handleAutoPlay(!selected),
            onDotClick: handleDotClick
          }}
        />
      </div>
    );
  });
export { CarouselProps };
